<!-- 
Contents of the LEVY CALCULATION tab of the levy collection setup page.
-->
<template>
  <div class="home">
    <our-heading 
      :breadcrumbs="getBreadcrumbs('setup')"
      :subtitle="statusDescription"
      title="Setup"
      subtitle-tooltip="Levy collection status"
      user-guide-topic="levy-collection-setup"
      user-guide-section="levy-calculation"
    >
      <template v-slot:navigator>
        <levy-collection-navigator :levy-collection-id="levyCollectionId" />
      </template>
    </our-heading>
    <tabs :levy-collection-id="levyCollectionId">
      <v-alert :value="publicEstimatorPublished && canEdit" type="info">
        Be careful making changes to these details. 
        <ul>
          <li>
            Changes to timing may affect which providers are in-scope and their levy parameters.
          </li>
          <li>
            Changes to fees &amp; rates or risk factors will affect calculations in the public levy estimator.
            If any levy estimate notices have been sent then those providers may also see changes in 
            the calculations in their levy estimator.
          </li>
        </ul>
      </v-alert>
      <h3 class="title mb-0 ml-4 mt-4">
        Data sources          
        <our-user-guide-button topic="levy-collection-setup" section="data-sources" in-title/>
      </h3>
      <data-sources-configuration-panel :levy-collection-id="levyCollectionId"/>
      <v-divider/>
      <h3 class="title mb-0 ml-4 mt-4">
        Timing
        <our-user-guide-button topic="levy-collection-setup" section="timing" in-title/>
      </h3>
      <levy-calculation-timing-configuration-panel :levy-collection-id="levyCollectionId" />
      <v-divider/>
      <h3 class="title mb-0 ml-4 mt-4">
        Fees &amp; rates
        <our-user-guide-button topic="levy-collection-setup" section="fees-rates" in-title/>
      </h3>
      <our-loading-indicator v-if="loading" />
      <fees-panel v-else :levy-collection-id="levyCollectionId"/>
      <v-divider/>
      <h3 class="title mb-0 ml-4 mt-4">
        Risk factors
        <our-user-guide-button topic="levy-collection-setup" section="risk-factors" in-title/>
      </h3>
      <v-layout row wrap>
        <div class="flex xs12 md6 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            :editing="editing[0]"
            type="FinancialStrength"
            title="Financial strength" 
            table-title="Financial strength score" 
            @update:editing="edit(0, $event)"
          />
        </div>
        <div class="flex xs12 md6 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            :editing="editing[1]"
            type="NetProfitRatio"
            title="Financial strength: Net profit ratio" 
            table-title="Net profit ratio" 
            terminology="Score"
            @update:editing="edit(1, $event)"
          />
        </div>
        <div class="flex xs12 md6 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            :editing="editing[2]"
            type="ReturnOnAssets"
            title="Financial strength: Return on assets" 
            table-title="Return on assets" 
            terminology="Score"
            @update:editing="edit(2, $event)"
          />
        </div>
        <div class="flex xs12 md6 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            :editing="editing[3]"
            type="DebtToEquity"
            title="Financial strength: Debt to equity" 
            table-title="Debt to equity" 
            terminology="Score"
            @update:editing="edit(3, $event)"
          />
        </div>
        <div class="flex xs12 md6 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            :editing="editing[4]"
            is-percentage
            type="CourseCompletion" 
            title="Completion rate" 
            @update:editing="edit(4, $event)" 
          />
        </div>
        <div v-if="!enableShortRegistrationPeriodRiskFactor" class="flex xs12 md6 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            :editing="editing[5]"
            type="NonComplianceHistory"
            title="Non-compliance history: Late payment value" 
            table-title="Late payment value" 
            @update:editing="edit(5, $event)"
          />
        </div>
        <div v-if="enableShortRegistrationPeriodRiskFactor" class="flex xs12 md6 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            :editing="editing[5]"
            type="NonComplianceHistory"
            title="Non-compliance history and registration renewal: Late payment value" 
            table-title="Late payment value" 
            @update:editing="edit(5, $event)"
          />
        </div>        
        <div v-if="enableShortRegistrationPeriodRiskFactor" class="flex xs12 md6 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            :editing="editing[6]"
            type="ShortRegistrationPeriod"
            title="Non-compliance history and registration renewal: Registration renewal value" 
            table-title="Registration renewal value" 
            @update:editing="edit(6, $event)"
          />
        </div>
      </v-layout>
      <v-layout row wrap>
        <div class="flex xs12 md6 pa-2">
          <h3 class="subheading mb-0 ml-4 mt-4">Other non-compliance history settings</h3>
          <non-compliance-history-configuration-panel :levy-collection-id="levyCollectionId"/>
        </div>
      </v-layout>
    </tabs>
  </div>
</template>

<script>
import _ from "underscore";
import DataSourcesConfigurationPanel from "@/components/levy-collection-setup/DataSourcesConfigurationPanel.vue";
import LevyCalculationTimingConfigurationPanel from "@/components/levy-collection-setup/LevyCalculationTimingConfigurationPanel.vue";
import FeesPanel from "@/components/levy-collection-setup/FeesPanel.vue";
import NonComplianceHistoryConfigurationPanel from "@/components/levy-collection-setup/NonComplianceHistoryConfigurationPanel.vue";
import RiskFactorTable from "@/components/levy-collection-risk-factors/RiskFactorTable.vue";
import LevyCollectionNavigator from "@/components/levy-collections/LevyCollectionNavigator.vue";
import Tabs from "./Tabs.vue";
import levyCollectionSetupMixins from "./mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as claim from "@/store/claim-types";

export default {
  components: {
    Tabs,
    LevyCollectionNavigator,
    RiskFactorTable,
    FeesPanel,
    DataSourcesConfigurationPanel,
    LevyCalculationTimingConfigurationPanel,
    NonComplianceHistoryConfigurationPanel
  },
  mixins: [levyCollectionSetupMixins, lookupMixins],
  data() {
    return {
      editing: [false, false, false, false, false, false, false]
    };
  },
  computed: {
    publicEstimatorPublished() {
      return this.levyCollection.publicEstimatorPublished;
    },
    enableShortRegistrationPeriodRiskFactor() {
      return this.levyCollection.enableShortRegistrationPeriodRiskFactor;
    },
    canEdit() {
      const hasPermission = this.$store.getters.claims.includes(claim.LEVY_COLLECTION_CONFIGURE_RISK_FACTORS);
      const status = this.levyCollection.status;
      const isAllowableStatus = this.doesLookupHaveAttribute(
        status,
        "CanEditRiskFactors",
        this.lookups.levyCollectionStatuses
      );
      return hasPermission && isAllowableStatus;
    }
  },
  methods: {
    edit(panel, edit) {
      _(this.editing).each((_, i) => this.$set(this.editing, i, i == panel && edit));
    }
  }
};
</script>
